import { PublicationStatus, ProjectType } from '@prisma/client';
import type { ImpactType } from '../../impact';
import type { PublicProject } from '../../projects';

const createProjectImage = (id: number, imageKey: string, alt: string): PublicProject['images'][number] => ({
  id,
  imageKey,
  alt,
});

const createProjectLocation = (
  id: number,
  region: string | null,
  country: string,
  latitude: number | null,
  longitude: number | null,
): PublicProject['locations'][number] => ({
  id,
  region,
  country,
  latitude: latitude as any,
  longitude: longitude as any,
  restorSiteId: null,
});

const baseProject = ({
  id,
  impactType,
  title,
  description,
  slug,
  projectType,
  projectSubType = null,
  images,
  locations,
  sdgNumbers,
}: {
  id: number;
  impactType: ImpactType;
  title: string;
  description: string;
  slug: string;
  projectType: ProjectType;
  projectSubType?: PublicProject['projectSubType'];
  images: PublicProject['images'];
  locations: PublicProject['locations'];
  sdgNumbers: number[];
}): PublicProject => ({
  id,
  impactType,
  title,
  slug,
  status: PublicationStatus.LIVE,
  description,
  whySupport: null,
  shortSnippet: description,
  longSnippet: description,
  projectType,
  projectSubType,
  impactFact: null,
  fundedSince: null,
  lastVisit: null,
  videoLink: null,
  climateBenefits: null,
  natureBenefits: null,
  peopleBenefits: null,
  projectAssetsLink: null,
  primaryImage: images[0] ?? null,
  images,
  locations,
  sdgs: sdgNumbers.map(sdgNumber => ({ sdgNumber, explanation: null })),
  updates: [],
  impactPartners: [],
  verificationPartners: [],
});

export const carbonRemovalProject: PublicProject = baseProject({
  id: 5706,
  impactType: 'CARBON_REMOVAL' as ImpactType,
  title: 'Biochar Carbon Removal',
  description: 'Capturing carbon by generating biochar',
  slug: 'biochar-carbon-removal',
  projectType: ProjectType.BIOCHAR,
  images: [
    createProjectImage(
      1,
      'https://cms-assets.offset.earth/app/uploads/20220128160818/Aleo-Manali-Hydropower3-1024x768.jpeg',
      'Biochar project image 1',
    ),
    createProjectImage(
      2,
      'https://cms-assets.offset.earth/app/uploads/20220128160824/Aleo-Manali-Hydropower4.jpeg',
      'Biochar project image 2',
    ),
    createProjectImage(
      3,
      'https://cms-assets.offset.earth/app/uploads/20220128160829/Aleo-Manali-Hydropower2.jpeg',
      'Biochar project image 3',
    ),
  ],
  locations: [createProjectLocation(1, null, 'Global', 32.22706664564586, 77.19825676919339)],
  sdgNumbers: [7, 8, 13],
});

export const carbonOffsetProject: PublicProject = baseProject({
  id: 97,
  impactType: 'CARBON_OFFSETS' as ImpactType,
  title: 'Improved cooking stoves in Kenya',
  description: 'The new stoves require less wood fuel (than traditional stoves) to cook a meal.',
  slug: 'improved-cooking-stoves-in-kenya',
  projectType: ProjectType.COMMUNITY,
  images: [
    createProjectImage(
      4,
      'https://cms-assets.offset.earth/app/uploads/20190723164944/IMG_5222.jpg-1024x683.jpg',
      'Cookstove project image 1',
    ),
    createProjectImage(
      5,
      'https://cms-assets.offset.earth/app/uploads/20190723165019/Kenya-Cook-Stove-6.jpg-1024x768.jpg',
      'Cookstove project image 2',
    ),
  ],
  locations: [createProjectLocation(2, 'Aberdares', 'Kenya', -0.790726, 37.086512)],
  sdgNumbers: [3, 13, 15],
});

export const treeProject: PublicProject = baseProject({
  id: 3503,
  impactType: 'TREES' as ImpactType,
  title: 'Forest restoration in Kenya',
  description: 'Forest restoration in Kenya',
  slug: 'forest-restoration-in-kenya',
  projectType: ProjectType.REFORESTATION,
  images: [
    createProjectImage(
      6,
      'https://cms-assets.offset.earth/app/uploads/20210720110739/20210310_114809-1024x576.jpeg',
      'Forest project image 1',
    ),
    createProjectImage(
      7,
      'https://cms-assets.offset.earth/app/uploads/20210720110807/20210310_115639-1024x576.jpeg',
      'Forest project image 2',
    ),
    createProjectImage(
      8,
      'https://cms-assets.offset.earth/app/uploads/20210720110815/20210310_132517-1024x576.jpeg',
      'Forest project image 3',
    ),
    createProjectImage(
      9,
      'https://cms-assets.offset.earth/app/uploads/20210720110823/20210311_083047-1024x576.jpeg',
      'Forest project image 4',
    ),
  ],
  locations: [createProjectLocation(3, 'Mau Region', 'Kenya', -0.3903347935702358, 35.51811983944913)],
  sdgNumbers: [1, 2, 3, 4, 5, 8, 13, 15],
});

export const habitatRestorationProject: PublicProject = baseProject({
  id: 4826,
  impactType: 'HABITAT_RESTORATION' as ImpactType,
  title: 'Restoring Wildflowers, Wetlands and Wildlife',
  description: 'Wildflower, Wetland and Wildlife restoration',
  slug: 'support-wildflowers-wetlands-and-wildlife',
  projectType: ProjectType.WILDFLOWERS,
  images: [
    createProjectImage(
      10,
      'https://cms-assets.offset.earth/app/uploads/20220128160818/Aleo-Manali-Hydropower3-1024x768.jpeg',
      'Wildflowers project image 1',
    ),
    createProjectImage(
      11,
      'https://cms-assets.offset.earth/app/uploads/20220128160824/Aleo-Manali-Hydropower4.jpeg',
      'Wildflowers project image 2',
    ),
  ],
  locations: [createProjectLocation(4, null, 'United Kingdom', 32.22706664564586, 77.19825676919339)],
  sdgNumbers: [7, 8, 13],
});
